// ContactCard — the head of the record zone on the standing brief (BUILD-BRIEF-R7 §6.1; the
// operator, 2026-09-06: «when I get into a record I don't see anything»).
//
// THE BASICS FIRST, EACH WITH ITS MARK AND ITS METHOD: e-mail(s), phone(s), LinkedIn, the canton
// and commune of work, the correspondence language, the register, the tier, the cadence, do not
// contact. A value is read from TWO places at once — the person's own COLUMNS (`persons.emails`,
// `.phones`, `.linkedin_url`, `.location`, `.register`, the settings) and the raster's CELLS
// (`contact.*`, `person.*` from GET /api/persons/:id/profile) — and the card says which:
//
//   ▪ declared      the operator typed it here — PATCH wrote a `declared` fact on the cell
//   ▪ read          a source said it, quoted, and a person or the rule held it
//   ▪ estimated     an agent inferred it and it was held; the ghost tag and the confidence say so
//   ◇               a proposal waits in Review, a current fact nobody has confirmed (the meta
//                   says «not held» beside the method), or a column an IMPORT wrote and nobody held
//   (no mark)       computed — arithmetic over the record, its basis under the value
//   not collected   nothing anywhere; never a blank, never a zero
//
// A column value with no fact behind it takes the ROW's own provenance as its mark: the operator
// created the row → ▪, an import did → ◇. That is the honest reading of a column nobody has
// filed a fact for, and it is the only place this file infers anything.
//
// EDITING IS INLINE AND WRITES THROUGH THE ONE DOOR: an edit calls `updatePerson` → PATCH, which
// writes the column AND a `declared` fact on the cell in one transaction (routes/persons.ts,
// §3.5). A value the cell refuses — a LinkedIn URL that is not `linkedin.com/in/`, a register
// that is neither `sie` nor `du` — comes back as a 400 whose sentence is shown under the field,
// unparaphrased. Clearing a field RETRACTS the claim (P4: a status, not a blank).
//
// THE REGISTER VOTE. While `persons.register` is unset, the writing profile's reading of the
// person's own lines is shown beside it as a ◇ suggestion with the number of lines behind it and
// one click, "Declare", that makes it the operator's own. Nothing is decided without that click.
//
// «ASK SURFE» IS A LIVE VERB (§5.2; this comment said «disabled» for a phase after the button was
// wired — the review of 07.09.2026). The click POSTs /api/persons/:id/enrich, which goes through
// the registry's `surfe_enrich_email` capability, so the operator's own call lands in `tool_calls`
// beside a run's and answers the same way. THE FAILOVER IS THE ANSWER, never an error: the route
// replies 200 with either a proposal ref now waiting in Review — a broker's assertion is never
// held on arrival — or the rung passed with its reason (`surfe_off`, `surfe_exhausted`,
// `surfe_limited`, `surfe_unreachable`, `no_current_affiliation`, `email_already_held`, …),
// printed in words under the button with the credits left. Nothing is bought when a rung passes.
// The button is disabled for ONE reason: `do_not_contact` — nothing is bought about a person who
// asked not to be contacted.
//
// SETTINGS CARRY NO CLAIM MARK. Tier, cadence and do-not-contact are the operator's own decisions
// about the relationship, not claims about the person — the mockup prints them plain, and so does
// this card; their meta reads "setting".
//
// THE LABELS COME FROM THE MODULE through `personLabelFor` — the cell's own name for every
// cell-backed row and every token (a canton, a register, a language). No second spelling here.
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { Att, Fig, Lnk, Ref, Sep, Sheet, Tag } from './PageKit.js';
import { IdentifierValue, PersonCellValue, cellOf } from './PersonRasterZone.js';
import { useLocale, useT } from '../i18n/index.js';
import { enrichPerson, updatePerson, type EnrichAnswer, type PersonInput } from '../api/persons.js';
import { personLabelFor } from '../../shared/person-raster.v1.js';
import type { RasterLang } from '../../shared/raster.v1.js';
import type {
  Person, PersonLanguage, PersonProfileCell, PersonProfileResponse, Register, Tier, WritingProfile,
} from '../../shared/types.js';
import './ContactCard.de.js';
import './ContactCard.fr.js';

export interface ContactCardProps {
  person: Person;
  /** The raster, once PersonRasterZone has read it; null until then and the card reads the columns alone. */
  profile: PersonProfileResponse | null;
  /** The writing profile, for the register vote; null until the mail service answers. */
  writing: WritingProfile | null;
  /** After a PATCH: the page refreshes the brief and asks the raster to re-read. */
  onSaved: () => void | Promise<void>;
}

/** The rows an operator can type into. Each is a PATCHable column; five of them are also cells. */
type Field = 'emails' | 'phones' | 'linkedin_url' | 'location' | 'language' | 'register' | 'tier' | 'cadence_days';

const LANGUAGES: readonly PersonLanguage[] = ['de', 'fr', 'en', 'it'];
const TIERS: readonly Tier[] = ['A', 'B', 'C'];
const REGISTERS: readonly Register[] = ['sie', 'du'];

/** The text an edit starts from: the column as it stands. Lists join with a comma. */
function draftOf(person: Person, field: Field): string {
  switch (field) {
    case 'emails': return person.emails.join(', ');
    case 'phones': return person.phones.join(', ');
    case 'linkedin_url': return person.linkedin_url ?? '';
    case 'location': return person.location ?? '';
    case 'language': return person.language;
    case 'register': return person.register ?? '';
    case 'tier': return person.tier;
    case 'cadence_days': return String(person.cadence_days);
  }
}

/** The PATCH a draft becomes. An emptied list is `[]` and an emptied text is `null`: both retract. */
function patchOf(field: Field, draft: string): Partial<PersonInput> {
  const v = draft.trim();
  switch (field) {
    case 'emails':
    case 'phones': {
      // Commas and semicolons separate; whitespace does not — a phone number has spaces in it.
      const list = v ? v.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean) : [];
      return field === 'emails' ? { emails: list } : { phones: list };
    }
    case 'linkedin_url':
      return { linkedin_url: v || null };
    case 'location':
      return { location: v || null };
    case 'register':
      return { register: (v || null) as Register | null };
    case 'tier':
      return { tier: v as Tier };
    case 'language':
      return { language: v as PersonLanguage };
    case 'cadence_days':
      return { cadence_days: Number(v) };
  }
}

export default function ContactCard({ person: p, profile, writing, onSaved }: ContactCardProps) {
  const t = useT();
  const { lang: uiLang, fmtDate, fmtNum, tp } = useLocale();
  const lang = uiLang as RasterLang;

  const [editing, setEditing] = useState<{ field: Field; draft: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<{ row: string; text: string } | null>(null);
  /** «Ask Surfe» (BUILD-BRIEF-R7 §5.2): the last answer, shown as a row until the next click. */
  const [surfe, setSurfe] = useState<{ busy: boolean; answer: EnrichAnswer | null; error: string | null }>({ busy: false, answer: null, error: null });

  /**
   * THE PAID RUNG, BY HAND. One POST; the server goes through the registry and its failover, so
   * the click lands in the ledger beside a run's call and answers the same way: a proposal ref
   * (a broker's assertion is always this operator's click in Review — never held on arrival) or
   * the rung passed with its reason. A proposal filed asks the page to re-read the raster, so the
   * cell shows ◇ pending.
   */
  const askSurfe = async (): Promise<void> => {
    setSurfe({ busy: true, answer: null, error: null });
    try {
      const answer = await enrichPerson(p.id);
      setSurfe({ busy: false, answer, error: null });
      if (answer.proposal_ref || (answer.filed?.length ?? 0) > 0) await onSaved();
    } catch (err) {
      setSurfe({ busy: false, answer: null, error: err instanceof Error ? err.message : String(err) });
    }
  };

  /** THE ONE WRITE PATH of this card. Returns whether the server took it. */
  const write = async (row: string, patch: Partial<PersonInput>): Promise<boolean> => {
    setBusy(row);
    setRefusal(null);
    try {
      await updatePerson(p.id, patch);
      await onSaved();
      return true;
    } catch (err) {
      // The server's own sentence — the cell's refusal, never a paraphrase.
      setRefusal({ row, text: err instanceof Error ? err.message : String(err) });
      return false;
    } finally {
      setBusy(null);
    }
  };

  const startEdit = (field: Field) => { setRefusal(null); setEditing({ field, draft: draftOf(p, field) }); };
  const cancelEdit = () => { setEditing(null); setRefusal(null); };
  const saveEdit = async () => {
    if (!editing) return;
    const ok = await write(editing.field, patchOf(editing.field, editing.draft));
    if (ok) setEditing(null);
  };

  const cells = {
    email: cellOf(profile, 'contact', 'email_work'),
    phone: cellOf(profile, 'contact', 'phone_work'),
    linkedin: cellOf(profile, 'contact', 'linkedin_url'),
    canton: cellOf(profile, 'contact', 'work_canton'),
    commune: cellOf(profile, 'contact', 'work_commune'),
    languages: cellOf(profile, 'person', 'languages'),
    register: cellOf(profile, 'person', 'register'),
  };

  /** A column value with no fact behind it: the row's own provenance is its mark. */
  const rowMark: 'held' | 'proposed' = p.created_by_kind === 'operator' ? 'held' : 'proposed';
  const rowMarkTitle = p.created_by_kind === 'operator'
    ? t('typed by the operator; no fact on the cell yet')
    : t('written by an import; nobody has held it');

  const editLink = (field: Field, title: string): ReactNode => (
    editing?.field === field ? null : <>{' '}<Lnk onClick={() => startEdit(field)} title={title}>{t('Edit')}</Lnk></>
  );

  const editor = (field: Field, kind: 'text' | 'number' | 'select', options?: Array<{ value: string; label: string }>, placeholder?: string, note?: string): ReactNode => (
    editing?.field === field ? (
      <Editor
        kind={kind}
        draft={editing.draft}
        onDraft={(draft) => setEditing((e) => (e ? { ...e, draft } : e))}
        options={options}
        placeholder={placeholder}
        busy={busy === field}
        onSave={() => { void saveEdit(); }}
        onCancel={cancelEdit}
        refusal={refusal?.row === field ? refusal.text : null}
        note={note}
      />
    ) : null
  );

  const registerVote = !p.register && writing?.register ? writing : null;

  return (
    <Sheet clip className="contact-card">
      <div className="sheet-head">
        <span className="u-label">{t('Contact')}</span>
        <span className="zone-note">{t('the person’s own columns and the raster’s contact cells · an edit writes a declared fact')}</span>
        <span className="zone-note zone-note--end">
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            disabled={surfe.busy || p.do_not_contact}
            title={p.do_not_contact
              ? t('This person asked not to be contacted; nothing is bought about them.')
              : t('Ask Surfe for the professional e-mail — one credit; the answer is a proposal you hold or refuse.')}
            onClick={() => { void askSurfe(); }}
          >
            {surfe.busy ? t('Asking…') : t('Ask Surfe')}
          </button>
        </span>
      </div>
      <div className="sheet--pad facts">
        {/* ── the last Surfe answer, while there is one ──────────────────── */}
        {(surfe.answer || surfe.error) && (
          <div className="fgroup">
            <Row label="Surfe" meta={surfe.answer?.credits ? <><Fig>{surfe.answer.credits.totalEmail}</Fig> {t('e-mail credits left')}</> : undefined}>
              {surfe.error
                ? <span className="fv--broken">{surfe.error}</span>
                : <SurfeAnswer answer={surfe.answer!} t={t} />}
            </Row>
          </div>
        )}
        {/* ── reach ─────────────────────────────────────────────────────── */}
        <div className="fgroup">
          <Row label={personLabelFor(lang, 'contact', 'email_work')} meta={<CellMeta cell={cells.email} column={p.emails.length > 0} person={p} fmtDate={fmtDate} />}>
            <ColumnValues cell={cells.email} values={p.emails} mark={rowMark} markTitle={rowMarkTitle} lang={lang} fmtNum={fmtNum} tp={tp} />
            {editLink('emails', t('Type one or more addresses; the first is the work e-mail the raster holds. Clearing retracts.'))}
            {editor('emails', 'text', undefined, t('one or more addresses, comma-separated'), t('Clearing a field retracts the claim; the record keeps its history.'))}
          </Row>
          <Row label={personLabelFor(lang, 'contact', 'phone_work')} meta={<CellMeta cell={cells.phone} column={p.phones.length > 0} person={p} fmtDate={fmtDate} />}>
            <ColumnValues cell={cells.phone} values={p.phones} mark={rowMark} markTitle={rowMarkTitle} lang={lang} fmtNum={fmtNum} tp={tp} />
            {editLink('phones', t('Type one or more numbers; the first is the work phone the raster holds. Clearing retracts.'))}
            {editor('phones', 'text', undefined, t('one or more numbers, comma-separated'), t('Clearing a field retracts the claim; the record keeps its history.'))}
          </Row>
          <Row label={personLabelFor(lang, 'contact', 'linkedin_url')} meta={<CellMeta cell={cells.linkedin} column={!!p.linkedin_url} person={p} fmtDate={fmtDate} />}>
            <ColumnValues cell={cells.linkedin} values={p.linkedin_url ? [p.linkedin_url] : []} mark={rowMark} markTitle={rowMarkTitle} lang={lang} fmtNum={fmtNum} tp={tp} />
            {editLink('linkedin_url', t('Paste the profile URL — linkedin.com/in/…; the profile itself is never read.'))}
            {editor('linkedin_url', 'text', undefined, 'https://www.linkedin.com/in/…', t('Clearing a field retracts the claim; the record keeps its history.'))}
          </Row>
          <Row
            label={personLabelFor(lang, 'contact', 'work_canton')}
            labelTitle={t('The canton is read by a run or computed from the employer; there is no column to type into.')}
            meta={<CellMeta cell={cells.canton} column={false} person={p} fmtDate={fmtDate} />}
            fd={cells.canton?.state === 'computed' && cells.canton.basis ? cells.canton.basis : undefined}
          >
            {cells.canton
              ? <PersonCellValue cell={cells.canton} lang={lang} fmtNum={fmtNum} tp={tp} />
              : <span className="fv--unknown">{t('not collected')}</span>}
          </Row>
          <Row
            label={personLabelFor(lang, 'contact', 'work_commune')}
            meta={<CellMeta cell={cells.commune} column={!!p.location} person={p} fmtDate={fmtDate} />}
            fd={cells.commune?.state === 'computed' && cells.commune.basis ? cells.commune.basis : undefined}
          >
            <ColumnValues cell={cells.commune} values={p.location ? [p.location] : []} mark={rowMark} markTitle={rowMarkTitle} lang={lang} fmtNum={fmtNum} tp={tp} />
            {editLink('location', t('The commune of the person’s place of work, as the source spells it — not a domicile. Clearing retracts.'))}
            {editor('location', 'text', undefined, t('the commune as the source spells it'), t('Clearing a field retracts the claim; the record keeps its history.'))}
          </Row>
        </div>

        {/* ── the relationship's settings ─────────────────────────────── */}
        <div className="fgroup">
          <Row
            label={t('language')}
            meta={t('setting')}
            fd={cells.languages && cells.languages.value && (cells.languages.state === 'held' || cells.languages.state === 'computed')
              ? <>{personLabelFor(lang, 'person', 'languages')}: <PersonCellValue cell={cells.languages} lang={lang} fmtNum={fmtNum} tp={tp} /></>
              : undefined}
          >
            {editing?.field === 'language' ? null : personLabelFor(lang, 'person', 'languages', p.language)}
            {editLink('language', t('The language every draft to this person is written in.'))}
            {editor('language', 'select', LANGUAGES.map((l) => ({ value: l, label: personLabelFor(lang, 'person', 'languages', l) })))}
          </Row>
          <Row label={personLabelFor(lang, 'person', 'register')} meta={<CellMeta cell={cells.register} column={!!p.register} person={p} fmtDate={fmtDate} />}>
            {editing?.field === 'register' ? null : p.register ? (
              <>
                {/* The cell's own mark when a fact stands on it — and a fact nobody confirmed is
                    ◇, not ▪ (the review of 07.09.2026); the row's provenance only where the
                    column speaks alone. */}
                <Att
                  kind={cells.register?.state === 'held' ? (cells.register.held ? 'held' : 'proposed') : rowMark}
                  title={cells.register?.state === 'held'
                    ? (cells.register.held ? undefined : t('a fact on the cell that nobody has held'))
                    : rowMarkTitle}
                />{' '}
                {personLabelFor(lang, 'person', 'register', p.register)}
              </>
            ) : (
              <span className="fv--unknown">{t('not set')}</span>
            )}
            {editLink('register', t('Formal or informal address — the writing profile suggests, you decide.'))}
            {editor('register', 'select', [
              { value: '', label: t('not set') },
              ...REGISTERS.map((r) => ({ value: r, label: personLabelFor(lang, 'person', 'register', r) })),
            ])}
            {registerVote && editing?.field !== 'register' && (
              <span className="fd">
                <Att kind="proposed" title={t('read from their own lines; nobody has held it')} />{' '}
                {personLabelFor(lang, 'person', 'register', registerVote.register as Register)}
                {registerVote.register_evidence !== null && <> · <Fig>{t('{n} lines', { n: registerVote.register_evidence })}</Fig></>}
                {' '}
                <Lnk
                  onClick={() => { void write('register', { register: registerVote.register }); }}
                  disabled={busy === 'register'}
                  title={t('Declare the register the correspondence supports; every draft to them is then written in it.')}
                >
                  {busy === 'register' ? t('Saving…') : t('Declare')}
                </Lnk>
                {refusal?.row === 'register' && <span className="fd fv--broken">{refusal.text}</span>}
              </span>
            )}
          </Row>
          <Row label={t('tier')} meta={t('setting')}>
            {editing?.field === 'tier' ? null : p.tier}
            {editLink('tier', t('A, B or C — how much of your attention this person gets; the cadence follows it.'))}
            {editor('tier', 'select', TIERS.map((x) => ({ value: x, label: x })))}
          </Row>
          <Row label={t('cadence')} meta={t('setting')}>
            {editing?.field === 'cadence_days' ? null : <Fig unit="d">{p.cadence_days}</Fig>}
            {editLink('cadence_days', t('Days between touches before this person counts as cooling.'))}
            {editor('cadence_days', 'number')}
          </Row>
          <Row label={t('do not contact')} meta={t('setting')} fd={refusal?.row === 'do_not_contact' ? <span className="fv--broken">{refusal.text}</span> : undefined}>
            {p.do_not_contact ? <Tag tone="broken">{t('yes')}</Tag> : t('no')}
            {' '}
            <Lnk
              onClick={() => { void write('do_not_contact', { do_not_contact: !p.do_not_contact }); }}
              disabled={busy === 'do_not_contact'}
              title={p.do_not_contact
                ? t('Lift the block: drafts and sequences may address this person again.')
                : t('Set the block: no draft and no sequence addresses this person until it is lifted.')}
            >
              {busy === 'do_not_contact' ? t('Saving…') : p.do_not_contact ? t('Lift') : t('Set')}
            </Lnk>
          </Row>
        </div>
      </div>
    </Sheet>
  );
}

// ── the pieces ──────────────────────────────────────────────────────────────

/** What a skip code says, in the console's language — the server's code, the card's sentence. */
const SKIP_SENTENCE: Record<NonNullable<EnrichAnswer['skipped']>, string> = {
  surfe_off: 'Paid Surfe enrichment is off (setting surfe.email_enrichment); nothing was bought.',
  surfe_exhausted: 'Surfe has no e-mail credits left; nothing was bought.',
  surfe_limited: 'Surfe refused the call (HTTP {n}); nothing was bought.',
  surfe_unreachable: 'Surfe could not be reached; nothing was bought.',
  surfe_error: 'Surfe answered an error (HTTP {n}); nothing was bought.',
  no_current_affiliation: 'No held current employer — Surfe is asked only about a person whose employer is on the record.',
  email_already_held: 'A work e-mail is already held; nothing was asked.',
  do_not_contact: 'This person asked not to be contacted; nothing is bought about them.',
  not_identifiable: 'Neither a LinkedIn URL nor a last name with an employer domain: Surfe cannot identify them.',
  surfe_no_email: 'Surfe knows no professional e-mail for this person; the answer is kept as a snapshot.',
  surfe_pending: 'Surfe is still working; nothing was filed yet.',
  surfe_failed: 'Surfe could not enrich this person; the call is on the ledger.',
};

/** The answer as one line: a proposal ref with its status, or the rung passed with its reason. */
export function SurfeAnswer({ answer, t }: { answer: EnrichAnswer; t: ReturnType<typeof useT> }) {
  if (answer.proposal_ref) {
    return (
      <>
        <Att kind="proposed" title={t('a broker’s assertion waits for your click; it is never held on arrival')} />{' '}
        <Ref>{answer.proposal_ref}</Ref>{' '}
        <span className="fd">
          {t('waits in Review')}
          {answer.validation_status ? <> · {t('Surfe says')} {answer.validation_status}</> : null}
          {(answer.filed?.length ?? 0) > 1 ? <> · <Fig>{answer.filed!.length - 1}</Fig> {t('more cells filed beside it')}</> : null}
        </span>
      </>
    );
  }
  const code = answer.skipped;
  const sentence = code ? t(SKIP_SENTENCE[code], { n: answer.status ?? '' }) : (answer.reason ?? '');
  return (
    <span className="fv--attend">
      {sentence}
      {(answer.filed?.length ?? 0) > 0 ? <> · <Fig>{answer.filed!.length}</Fig> {t('cells filed from the answer')}</> : null}
    </span>
  );
}

function Row({ label, labelTitle, children, meta, fd }: {
  label: ReactNode;
  labelTitle?: string;
  children: ReactNode;
  meta?: ReactNode;
  /** A sentence under the value — a computed cell's basis, a suggestion, a refusal. */
  fd?: ReactNode;
}) {
  return (
    <div className="fact">
      <span className="fk" title={labelTitle}>{label}</span>
      <span>
        {children}
        {fd != null && <span className="fd">{fd}</span>}
      </span>
      <span className="fmeta">{meta}</span>
    </div>
  );
}

/**
 * The column's values, with the cell in front when the raster holds one. The cell renders through
 * `PersonCellValue` — the one dress for the six states — and the column values the cell does not
 * already say follow it, each with the row's provenance mark. Nothing anywhere reads
 * "not collected", `fv--unknown`, never a blank.
 */
function ColumnValues({ cell, values, mark, markTitle, lang, fmtNum, tp }: {
  cell: PersonProfileCell | null;
  values: string[];
  mark: 'held' | 'proposed';
  markTitle: string;
  lang: RasterLang;
  fmtNum: (n: number | string | null | undefined) => string;
  tp: (n: number, one: string, other: string) => string;
}) {
  const t = useT();
  const cellSpeaks = !!cell && cell.state !== 'open' && cell.state !== 'not_applicable';
  const rest = cellSpeaks ? values.filter((v) => v !== cell!.value) : values;
  if (!cellSpeaks && rest.length === 0) return <span className="fv--unknown">{t('not collected')}</span>;
  return (
    <>
      {cellSpeaks && <PersonCellValue cell={cell!} lang={lang} fmtNum={fmtNum} tp={tp} />}
      {rest.map((v, i) => (
        <span key={v}>
          {(cellSpeaks || i > 0) && <>{' '}<Sep />{' '}</>}
          <Att kind={mark} title={markTitle} />{' '}<IdentifierValue value={v} />
        </span>
      ))}
    </>
  );
}

/** The meta column: the method and the day for a cell, or the column's own provenance. */
function CellMeta({ cell, column, person, fmtDate }: {
  cell: PersonProfileCell | null;
  column: boolean;
  person: Person;
  fmtDate: (iso: string | null | undefined) => string;
}) {
  const t = useT();
  if (cell && cell.state === 'held' && cell.method) {
    return (
      <>
        {cell.method === 'estimated' && cell.confidence != null
          ? <>{t('estimated')} · {t('confidence')} <Fig>{cell.confidence.toFixed(2)}</Fig></>
          : t(cell.method)}
        {/* A current fact is not a held one: say so in words beside the ◇ the value carries. */}
        {!cell.held && <> · <span title={t('a fact on the cell that nobody has held')}>{t('not held')}</span></>}
        {cell.as_of && <> · <Fig>{fmtDate(cell.as_of)}</Fig></>}
      </>
    );
  }
  if (cell && cell.state === 'computed') {
    return <>{t('computed')}{cell.as_of && <> · <Fig>{fmtDate(cell.as_of)}</Fig></>}</>;
  }
  if (column) return <>{t('column')} · {t(person.created_by_kind)}</>;
  return null;
}

/** The in-place control: a line to type on, or a select, with Save · Cancel and the refusal under it. */
function Editor({ kind, draft, onDraft, options, placeholder, busy, onSave, onCancel, refusal, note }: {
  kind: 'text' | 'number' | 'select';
  draft: string;
  onDraft: (v: string) => void;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  busy: boolean;
  onSave: () => void;
  onCancel: () => void;
  refusal: string | null;
  note?: string;
}) {
  const t = useT();
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); onSave(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };
  return (
    <>
      <span className="fedit">
        {kind === 'select' ? (
          <select className="form-select" value={draft} autoFocus disabled={busy} onChange={(e) => onDraft(e.target.value)} onKeyDown={onKey}>
            {(options ?? []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input
            className="form-input"
            type={kind === 'number' ? 'number' : 'text'}
            min={kind === 'number' ? 1 : undefined}
            value={draft}
            autoFocus
            disabled={busy}
            placeholder={placeholder}
            onChange={(e) => onDraft(e.target.value)}
            onKeyDown={onKey}
          />
        )}
        <Lnk onClick={onSave} disabled={busy}>{busy ? t('Saving…') : t('Save')}</Lnk>
        <Lnk onClick={onCancel} disabled={busy}>{t('Cancel')}</Lnk>
      </span>
      {refusal
        ? <span className="fd fv--broken">{refusal}</span>
        : note ? <span className="fd">{note}</span> : null}
    </>
  );
}
